import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { createCodingChallenge, deleteCodingChallenge, listCodingChallenges } from '../api/coding';

const inputCls =
  'w-full border border-line bg-surface rounded-lg px-3 py-2 text-sm text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 placeholder:text-ink-faint';

const EMPTY_FORM = {
  title: '',
  description: '',
  language: 'javascript',
  difficulty: 'medium',
  starterCode: '',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function difficultyClass(d) {
  const v = (d || '').toLowerCase();
  if (v === 'easy') return 'chip-teal';
  if (v === 'hard') return 'chip-rose';
  return 'chip-amber';
}

export default function CodingChallengesPage() {
  const toast = useToast();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCodingChallenges();
      setChallenges(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load challenges');
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSaving(true);
    try {
      await createCodingChallenge({
        title: form.title.trim(),
        description: form.description.trim(),
        language: form.language,
        difficulty: form.difficulty,
        starterCode: form.starterCode || undefined,
      });
      toast.success('Challenge created');
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this challenge?')) return;
    try {
      await deleteCodingChallenge(id);
      toast.success('Challenge deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Delete failed');
    }
  }

  return (
    <div className="page-pad page-y max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Coding Challenges</h1>
          <p className="text-sm text-ink-faint mt-1">
            Manage challenges for post-interview coding rounds (includes global defaults)
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="btn btn-primary text-xs font-semibold px-4 py-2"
        >
          + Add Challenge
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading challenges…" />
          </div>
        ) : challenges.length === 0 ? (
          <p className="text-sm text-ink-faint text-center py-12">No challenges yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-faint border-b border-line bg-canvas">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Language</th>
                  <th className="px-4 py-3">Difficulty</th>
                  <th className="px-4 py-3">Scope</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {challenges.map((c) => (
                  <tr key={c.id} className="hover:bg-teal-50/20 transition-colors">
                    <td className="px-4 py-3 text-ink font-semibold">{c.title}</td>
                    <td className="px-4 py-3 text-ink-soft capitalize font-mono text-xs">{c.language}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold capitalize ${difficultyClass(c.difficulty)}`}>
                        {c.difficulty}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-faint text-xs">
                      {c.orgId ? 'Organization' : 'Global'}
                    </td>
                    <td className="px-4 py-3 text-ink-faint font-mono text-xs">{formatDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {c.orgId ? (
                        <button
                          type="button"
                          onClick={() => handleDelete(c.id)}
                          className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="text-xs text-ink-faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Challenge">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Title</label>
            <input className={inputCls} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Description</label>
            <textarea className={inputCls} rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Language</label>
              <select className={inputCls} value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="cpp">C++</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Difficulty</label>
              <select className={inputCls} value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Starter code</label>
            <textarea className={`${inputCls} font-mono text-xs`} rows={8} value={form.starterCode} onChange={(e) => setForm((f) => ({ ...f, starterCode: e.target.value }))} placeholder="// Your starter template" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50">
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
